import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useScopeParams } from "@/hooks/useScopeParams";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import type { InvestigatorSite } from "@/hooks/useInvestigatorSitesQuery";

/**
 * The investigator's sites as a list, in the shape the trial Sites tab uses.
 * The map alternative only plots sites that carry coordinates, so it silently
 * drops the rest; every site has a name and a trial count, so the table shows
 * all of them.
 */
export default function SiteLocationsTable({
  sites,
}: {
  sites: InvestigatorSite[];
}) {
  const { buildUrl } = useScopeParams();
  const profileHidden = useFeatureFlag(FEATURE_FLAGS.SITE_PROFILE_LINKS_HIDDEN);

  const columns: ColumnsType<InvestigatorSite> = [
    {
      title: "Site Name",
      key: "site_name",
      render: (_, row) => {
        const name = row.site_name ?? row.site_key;
        return profileHidden ? (
          <span className="text-[13px] font-medium text-[#1F2933]">{name}</span>
        ) : (
          <Link
            to={buildUrl(`site-profiles/${row.site_key}`)}
            className="text-[13px] font-medium !text-[#1F2933] hover:!text-[#0C98D0]"
          >
            {name}
          </Link>
        );
      },
    },
    {
      title: "Location",
      key: "location",
      width: 220,
      render: (_, row) => {
        const location = [row.city, row.country_code]
          .filter(Boolean)
          .join(", ");
        return (
          <span className="text-[13px] text-[#4B5563]">{location || "—"}</span>
        );
      },
    },
    {
      title: "Trials",
      key: "trial_count",
      width: 100,
      align: "center",
      render: (_, row) => (
        <span className="text-[13px] font-semibold text-[#1F2933]">
          {row.trial_count}
        </span>
      ),
    },
  ];

  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] overflow-x-auto">
      <Table<InvestigatorSite>
        dataSource={sites}
        columns={columns}
        rowKey="site_key"
        pagination={false}
        size="middle"
      />
    </div>
  );
}
